/**
 * Reports — the hub. Spec §M, T-6.7; redrawn as a hub of cards in Wave 15A
 * (C14 §10.6 item 1, ADR-0033 §1).
 *
 * **The list is the server's answer, not a filter over a constant.** The
 * service gates each report on the verbs its own definition names and returns
 * exactly what this caller may open; this screen renders that. A local list of
 * seven with a `can()` beside each would be a second copy of the matrix, and
 * the copy in the browser is the one that goes stale. The groups below only
 * *place* what the server returned.
 *
 * What it adds is the **refusal**: `meta.unavailable` names, per report, the
 * `(resource, verb)` pairs the caller is short of. A reviewer sees the four
 * money reports locked with "needs invoice:can_view", which is something an
 * administrator can act on — where a hidden row is a support ticket that starts
 * "I can't find the revenue report".
 */

import { Alert, Badge, EmptyState, PageHeader, Skeleton } from '@fiveox/ui'
import type { LucideIcon } from 'lucide-react'
import {
  BarChart3,
  ClipboardCheck,
  Coins,
  Gauge,
  Hourglass,
  Lock,
  Target,
  TrendingUp,
} from 'lucide-react'
import { useEffect, useState } from 'react'

import { HubCard, HubGroup } from '../../components/HubCard'
import { ApiError } from '../../lib/api'
import {
  type ReportKey,
  type ReportSummary,
  type ReportUnavailable,
  accepts,
  reportIndexMeta,
  reports,
  unavailableReason,
} from '../../lib/api/reports'

/** Where each report sits on the hub, and its icon. Placement only. */
const GROUPS: Array<{ key: string; title: string; description: string; reports: ReportKey[] }> = [
  {
    key: 'work',
    title: 'Work',
    description: 'What is due, what is done, and who did it.',
    reports: ['compliance', 'productivity'],
  },
  {
    key: 'money',
    title: 'Money',
    description: 'What clients owe you, what came in, and what you earned.',
    reports: ['receivables', 'collections', 'revenue'],
  },
  {
    key: 'growth',
    title: 'Growth',
    description: 'Enquiries, proposals and what they turned into.',
    reports: ['pipeline', 'sales'],
  },
]

const ICONS: Record<ReportKey, LucideIcon> = {
  compliance: ClipboardCheck,
  productivity: Gauge,
  receivables: Hourglass,
  collections: Coins,
  revenue: TrendingUp,
  pipeline: Target,
  sales: BarChart3,
}

/** The card's one line: what the report lets you narrow by. */
export function filterLine(key: ReportKey): string {
  if (accepts(key, 'period')) return 'Choose a period or a date range'
  if (accepts(key, 'from')) return 'Choose a date range'
  return 'As things stand today'
}

function LockedCard({ row }: { row: ReportUnavailable }) {
  const Icon = ICONS[row.key] ?? Lock
  return (
    <div
      data-locked={row.key}
      className="border-border flex h-full min-w-0 flex-col rounded-2xl border bg-card p-5 shadow-sm"
    >
      <div className="flex items-start gap-4">
        <span className="bg-muted text-muted-foreground flex h-11 w-11 shrink-0 items-center justify-center rounded-xl">
          <Icon size={20} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-muted-foreground text-base font-semibold">{row.title}</h3>
            <Badge tone="neutral" size="sm">
              <Lock size={12} aria-hidden /> Locked
            </Badge>
          </div>
          {/* The verb, not just the absence: it is what somebody would have
              to be granted. */}
          <p className="text-muted-foreground mt-1 text-sm">{unavailableReason(row)}</p>
        </div>
      </div>
    </div>
  )
}

export function Reports() {
  const [rows, setRows] = useState<ReportSummary[]>([])
  const [unavailable, setUnavailable] = useState<ReportUnavailable[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    reports
      .index()
      .then((body) => {
        if (cancelled) return
        const meta = reportIndexMeta(body.meta)
        setRows(body.data)
        setUnavailable(meta.unavailable)
        setTotal(meta.total)
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'The reports could not be listed.')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const placed = new Set(GROUPS.flatMap((g) => g.reports))
  // A report the server adds before this hub knows its group still shows.
  const others = rows.filter((row) => !placed.has(row.key))
  const groups = [
    ...GROUPS.map((group) => ({
      ...group,
      cards: group.reports
        .map((key) => rows.find((row) => row.key === key))
        .filter((row): row is ReportSummary => row !== undefined),
    })),
    { key: 'other', title: 'Other', description: '', reports: [], cards: others },
  ].filter((group) => group.cards.length > 0)

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8">
      <PageHeader
        title="Reports"
        description={
          loading
            ? 'Every report reads your records as they are now.'
            : `${rows.length} of ${total} reports are open to you. Each opens as a table you can search and export to CSV or Excel.`
        }
      />

      {error && <Alert tone="error">{error}</Alert>}

      {loading && (
        <div
          role="status"
          aria-busy
          aria-label="Loading"
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
        >
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-36 rounded-2xl" />
          ))}
        </div>
      )}

      {!loading && !error && rows.length === 0 && unavailable.length === 0 && (
        <EmptyState
          title="No reports yet"
          description="Reports appear here as your role is given the verbs they read. Ask a partner if you expected one."
        />
      )}

      {groups.map((group) => (
        <HubGroup
          key={group.key}
          id={`reports-${group.key}`}
          title={group.title}
          description={group.description || undefined}
          columns={group.cards.length % 3 === 0 || group.cards.length === 1 ? 3 : 2}
        >
          {group.cards.map((row) => (
            <HubCard
              key={row.key}
              cardKey={row.key}
              href={`/reports/${row.key}`}
              icon={ICONS[row.key] ?? BarChart3}
              title={row.title}
              description={row.summary}
              loading={false}
              line={{ tone: 'set', line: filterLine(row.key) }}
            />
          ))}
        </HubGroup>
      ))}

      {unavailable.length > 0 && (
        <HubGroup
          id="reports-locked"
          title="Not open to your role"
          description="Each says what your role would need. A partner can grant it."
          columns={unavailable.length % 3 === 0 || unavailable.length === 1 ? 3 : 2}
        >
          {unavailable.map((row) => (
            <LockedCard key={row.key} row={row} />
          ))}
        </HubGroup>
      )}
    </div>
  )
}
